import React, { useEffect, useState } from 'react';
import { useUserProfile } from '../../../hooks/useUserProfile';
import { usePotentialMatchCount } from '../../../hooks/usePotentialMatchCount';
import { Input, Textarea, Button, Checkbox, Dropdown, Collapse, Select, Divider, FileInput, Avatar, Card } from "react-daisyui";
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { AVAILABLE_DOCUMENTS } from '../../../models/UserProfile';
import { storage, supabase } from '../../../lib/supabase';
import { useAuth } from '../../../modules/authentication/AuthProvider';
import routes from '../../../modules/routes/Routes';
import { Link } from 'react-router-dom';
import { NewspaperPreview } from './NewspaperPost';
import { PublicProfileProvider, usePublicProfile } from '../../../hooks/usePublicProfile';
import { useNewspaperRequest } from '../../../hooks/useNewspaperRequest';

const NewspaperPostPrompt: React.FC = () => {
    const { publicProfile } = usePublicProfile();
    const { newspaperRequest } = useNewspaperRequest();

    return (
        <Card className="bg-base-200 shadow-lg mb-4">
            <Card.Body>
                <div className="flex flex-col">
                    <div className="mb-2">
                        <h3 className="text-2xl font-bold mb-2">Create Your Newspaper Post</h3>
                        <p className="mb-2">Get more visibility for your apartment search by creating a newspaper post that will be published in local newspapers.</p>
                    </div>

                    {/*<div className="bg-white p-3 rounded-lg shadow-md max-w-sm mb-3">
                        <div className="text-sm text-gray-500 mb-1">Newspaper Preview</div>
                        <div className="font-serif">
                            <h4 className="font-bold mb-1">Wohnungssuche</h4>
                            <p className="text-sm">
                                Young professional couple seeking 2-3 room apartment in Berlin, up to 1500€ warm. 
                                Both employed with permanent contracts. Non-smokers, no pets. 
                                Available for viewings on short notice. Contact: 0123-456789
                            </p>
                        </div>
                    </div>*/}

                    <div>
                        <NewspaperPreview className='bg-white' title={newspaperRequest?.title} description={newspaperRequest?.description} linkKey={publicProfile?.link_keys?.[0]}/>
                    </div>

                    <div className='mt-2'>
                        <Link to={routes.app.profile.searchPosts.build()}>
                            <Button color="primary" size="sm" data-umami-event="Profile Newspaper-Post-Setup">
                                Create Newspaper Post
                            </Button>
                        </Link>
                    </div>
                </div>
            </Card.Body>
        </Card>
    );
};



const PersonalProfile: React.FC = () => {
    const { localProfile, handleLocalProfileChange } = useUserProfile();
    const { t } = useTranslation();
    const { currentUser } = useAuth();
    const [moveInDate, setMoveInDate] = useState('');

    useEffect(() => {
        if (localProfile?.move_in_date) {
            const date = new Date(localProfile.move_in_date);
            if (!isNaN(date.getTime())) {
                setMoveInDate(date.toISOString().split('T')[0]);
            }
        }
    }, [localProfile?.move_in_date]);

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !currentUser?.id) return;

        try {
            // Upload image to Supabase Storage
            const fileExt = file.name.split('.').pop();
            const fileName = `${currentUser.id}.${fileExt}`;

            // Upload new file
            const { data, error } = await storage.profilePictures()
                .upload(fileName, file, {
                    upsert: true
                });

            if (error) throw error;

            // Get public URL
            const { data: { publicUrl } } = storage.profilePictures().getPublicUrl(fileName);

            // Update profile with new image URL
            handleLocalProfileChange('profile_picture_url', publicUrl);
        } catch (error) {
            console.error('Error uploading image:', error);
        }
    };

    if (localProfile === undefined) {
        return null; // or return a loading indicator
    }

    return (
        <>
            <h1 className="text-3xl font-bold mb-6">
                {t('profilePages.personalProfile.title')}
                &nbsp;<span className="text-sm text-gray-500">{t('profilePages.personalProfile.optional')}</span>
            </h1>
            <div className="space-y-8">
                <div>
                    <h2 className="text-xl font-semibold mb-4">{t('profilePages.personalProfile.forWhom.title')}</h2>
                    <div className="space-y-1">
                        <div>
                            <label className="label">
                                <span className="label-text">{t('profilePages.personalProfile.forWhom.name')}</span>
                            </label>
                            <Input
                                placeholder="Max Mustermann"
                                className="w-full"
                                value={localProfile.persons?.[0]?.name || ''}
                                onChange={(e) => {
                                    const newPersons = [...localProfile.persons ?? []];
                                    newPersons[0] = { ...newPersons[0], name: e.target.value };
                                    handleLocalProfileChange('persons', newPersons);
                                }}
                            />
                        </div>
                        <div>
                            <label className="label">
                                <span className="label-text">{t('profilePages.profileDetails.personalDetails.gender')}</span>
                            </label>
                            <Select 
                                className="w-full"
                                value={localProfile.persons?.[0]?.gender || ''}
                                onChange={(e) => {
                                    const newPersons = [...(localProfile.persons || [])];
                                    newPersons[0] = { ...newPersons[0], gender: e.target.value as 'male' | 'female' | 'other' };
                                    handleLocalProfileChange('persons', newPersons);
                                }}
                            >
                                <option value="male">{t('profilePages.profileDetails.personalDetails.genderOptions.male')}</option>
                                <option value="female">{t('profilePages.profileDetails.personalDetails.genderOptions.female')}</option>
                                <option value="other">{t('profilePages.profileDetails.personalDetails.genderOptions.other')}</option>
                            </Select>
                        </div>
                        <div>
                            <label className="label">
                                <span className="label-text">{t('profilePages.personalProfile.forWhom.age')}</span>
                            </label>
                            <Input
                                type="number"
                                className="w-full"
                                value={localProfile.persons?.[0]?.age?.toString() || ''}
                                onChange={(e) => {
                                    const newPersons = [...localProfile.persons ?? []];
                                    newPersons[0] = { ...newPersons[0], age: parseInt(e.target.value, 10) };
                                    handleLocalProfileChange('persons', newPersons);
                                }}
                            />
                        </div>
                        <div>
                            <label className="label">
                                <span className="label-text">{t('profilePages.personalProfile.forWhom.occupation')}</span>
                            </label>
                            <Input
                                value={localProfile.persons?.[0]?.occupation || ''}
                                className="w-full"
                                onChange={(e) => {
                                    const newPersons = [...localProfile.persons ?? []];
                                    newPersons[0] = { ...newPersons[0], occupation: e.target.value };
                                    handleLocalProfileChange('persons', newPersons);
                                }}
                            />
                        </div>
                        <div>
                            <label className="label">
                                <span className="label-text">Profile Picture</span>
                            </label>
                            <div className="flex items-center space-x-4">
                                {localProfile.profile_picture_url && (
                                    <Avatar
                                        size="lg"
                                        innerClassName="rounded"
                                        color="neutral"
                                        src={localProfile.profile_picture_url || undefined}
                                        letters={localProfile.persons?.[0]?.name?.split(' ').map(n => n[0]).join('') || ''}
                                    />
                                )}
                                <FileInput
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    className="w-full"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                
                {(currentUser?.email?.includes('niklasgromann') || currentUser?.email?.includes('demler') || currentUser?.email?.includes('@test')) ? 
                    <PublicProfileProvider userId={currentUser?.id}>
                        <NewspaperPostPrompt/>
                    </PublicProfileProvider>
                : 
                    <Divider/>
                }

                <div className="space-y-2">
                    <div>
                        <label className="label">
                            <span className="label-text">{t('profilePages.profileDetails.additionalDetails.moveInDate')}</span>
                        </label>
                        <Input
                            type="date"
                            className="w-full"
                            value={moveInDate}
                            onChange={(e) => {
                                setMoveInDate(e.target.value);
                                if (e.target.value) {
                                    handleLocalProfileChange('move_in_date', new Date(e.target.value));
                                }
                            }}
                        />
                    </div>

                    <div>
                        <label className="label justify-start">
                            <span className="label-text">{t('profilePages.profileDetails.additionalDetails.stayDuration')}</span>
                            <span className="text-sm text-gray-500 ml-2">{t('profilePages.personalProfile.optional')}</span>
                        </label>
                        <Input
                            value={localProfile.length_of_stay || ''}
                            placeholder={t('profilePages.profileDetails.additionalDetails.stayDurationPlaceholder')}
                            className="w-full"
                            onChange={(e) => handleLocalProfileChange('length_of_stay', e.target.value)}
                        />
                    </div>

                    <div>
                        <Collapse.Details className='bg-base-200 mt-4'>
                            <Collapse.Details.Title className="text-lg font-semibold">
                                <div className="flex items-center gap-2">
                                    <ChevronDown className="collapse-closed w-5 h-5" />
                                    <ChevronUp className="collapse-opened w-5 h-5" />
                                    <span>{t('profilePages.personalProfile.reasonsForMoving.title')}</span><span className="text-sm text-gray-500">{t('profilePages.personalProfile.optional')}</span>
                                </div>
                            </Collapse.Details.Title>
                            <Collapse.Content className="space-y-1">
                                {t('profilePages.personalProfile.reasonsForMoving.reasons', { returnObjects: true }).map((reason, index) => (
                                    <div key={index} className="form-control">
                                        <label className="label cursor-pointer">
                                            <span className="label-text">{reason}</span>
                                            <Checkbox
                                                checked={(localProfile.reason_for_moving || '').includes(reason)}
                                                onChange={(e) => {
                                                    const reasons = (localProfile.reason_for_moving || '').split(', ').filter(Boolean);
                                                    if (e.target.checked) {
                                                        reasons.push(reason);
                                                    } else {
                                                        const index = reasons.indexOf(reason);
                                                        if (index > -1) reasons.splice(index, 1);
                                                    }
                                                    handleLocalProfileChange('reason_for_moving', reasons.join(', '));
                                                }}
                                            />
                                        </label>
                                    </div>
                                ))}
                            </Collapse.Content>
                        </Collapse.Details>
                    </div>

                    <div>
                        <Collapse.Details className='bg-base-200 mt-4'>
                            <Collapse.Details.Title className="text-lg font-semibold">
                                <div className="flex items-center gap-2">
                                    <ChevronDown className="collapse-closed w-5 h-5" />
                                    <ChevronUp className="collapse-opened w-5 h-5" />
                                    <span>{t('profilePages.personalProfile.currentSituation.title')}</span><span className="text-sm text-gray-500">{t('profilePages.personalProfile.optional')}</span>
                                </div>
                            </Collapse.Details.Title>
                            <Collapse.Content className="space-y-1">
                                {t('profilePages.personalProfile.currentSituation.situations', { returnObjects: true }).map((situation, index) => (
                                    <div key={index} className="form-control">
                                        <label className="label cursor-pointer">
                                            <span className="label-text">{situation}</span>
                                            <Checkbox
                                                checked={(localProfile.current_situation || '').includes(situation)}
                                                onChange={(e) => {
                                                    const situations = (localProfile.current_situation || '').split(', ').filter(Boolean);
                                                    if (e.target.checked) {
                                                        situations.push(situation);
                                                    } else {
                                                        const index = situations.indexOf(situation);
                                                        if (index > -1) situations.splice(index, 1);
                                                    }
                                                    handleLocalProfileChange('current_situation', situations.join(', '));
                                                }}
                                            />
                                        </label>
                                    </div>
                                ))}
                            </Collapse.Content>
                        </Collapse.Details>
                    </div>

                </div>

                <Divider/>

                <div className="space-y-2">
                    <h3 className="text-lg font-semibold">{t('profilePages.personalProfile.availableDocuments.title')}</h3>
                    <p className="text-sm text-gray-600">{t('profilePages.personalProfile.availableDocuments.explanation')}</p>
                    <div className="space-y-4">
                        {Object.entries(AVAILABLE_DOCUMENTS).map(([key, value]) => {
                            const isChecked = (localProfile.documents_available || []).includes(value);
                            const toggleDocument = () => {
                                const documents = [...(localProfile.documents_available || [])];
                                if (!isChecked) {
                                    documents.push(value);
                                } else {
                                    const index = documents.indexOf(value);
                                    if (index > -1) documents.splice(index, 1);
                                }
                                handleLocalProfileChange('documents_available', documents);
                            };

                            return (
                                <div 
                                    key={key} 
                                    className="flex items-center p-4 border rounded-lg bg-base-200 cursor-pointer hover:bg-base-300"
                                    onClick={toggleDocument}
                                >
                                    <div className="mr-4">
                                        <FileText className="h-6 w-6" />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="font-semibold">{t(`profilePages.personalProfile.availableDocuments.documents.${value}.title`)}</h4>
                                        <p className="text-sm text-gray-600">{t(`profilePages.personalProfile.availableDocuments.documents.${value}.description`)}</p>
                                    </div>
                                    <div onClick={e => e.stopPropagation()}>
                                        <Checkbox
                                            checked={isChecked}
                                            onChange={toggleDocument}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <Divider/>
        
                <div>
                    <label className="label">
                        <span className="label-text">{t('profilePages.profileDetails.hobbiesInterests.interests')}</span>
                    </label>
                    <Textarea
                        className="w-full"
                        value={localProfile.interests || ''}
                        onChange={(e) => handleLocalProfileChange('interests', e.target.value)}
                    />
                </div>
            </div>
        </>
    );
};

export default PersonalProfile;